import { handleActions } from 'redux-actions'
import { fromJS, List, Map } from 'immutable'
import { REHYDRATE } from 'redux-persist'

import { USERS_TEAM_FILTER } from 'app/constants/organizations'
import { ReviewsMessaging, ReviewsUser, Messaging, ReviewersUser } from 'store/records/reviews'
import { STATUS_FILTER } from 'app/constants/reviews'
import { deleteOrganizationUserBulk, updateUserTeamsSuccessForReviews } from 'store/entities/organizations/actions'
import { PER_PAGE } from 'app/constants/pagination'

import {
  setFilterBySearch,
  setFilterByTeam,
  setFilterByStatus,
  getReviewsUsers,
  getReviewsMessaging,
  postOrPutReviewsMessaging,
  postReviewsUserReviewers,
  selectPage,
  fetchReviewsReport,
  postReviewsReportBulk,
  setFilterBySort,
  updateReviewUserEmail,
} from './actions'
import { EnumDeleteUserFrom } from 'app/domains/common/AdminAndSuperAdminComponents/DeleteUserForm/interface'
import { REVIEWS } from 'app/constants/roles'

export const initialState = fromJS({
  messaging: new ReviewsMessaging(),
  filter: {
    byTeam: USERS_TEAM_FILTER.ALL,
    bySearch: '',
    byStatus: STATUS_FILTER.ALL,
    bySort: {
      by: null,
      desc: false,
    },
  },
  pagination: {
    current: 0,
    perPage: PER_PAGE,
  },
  users: List(),
  helper: Map(),
  report: Map({
    pending: false,
    data: Map(),
  }),
})

const handleRehydrate = state => {
  return state
}

const handleSetFilterBySearch = (state, { payload }) => {
  return state.setIn(['filter', 'bySearch'], payload).setIn(['pagination', 'current'], 0)
}

const handleSetFilterByTeam = (state, { payload }) => {
  return state.setIn(['filter', 'byTeam'], payload).setIn(['pagination', 'current'], 0)
}

const handleSetFilterByStatus = (state, { payload }) => {
  return state.setIn(['filter', 'byStatus'], payload).setIn(['pagination', 'current'], 0)
}

const handleSetFilterBySort = (state, { payload }) => {
  return state.setIn(['filter', 'bySort', 'by'], payload.by).setIn(['filter', 'bySort', 'desc'], payload.desc)
}

const handleGetReviewsUsersSuccess = (state, { payload }) => {
  return state.set(
    'users',
    List(
      payload
        .filter(user => user.roles.filter(role => role.name === REVIEWS))
        .map(user => new ReviewsUser(fromJS(user)))
    )
  )
}

const handleGetReviewsMessagingSuccess = (state, { payload }) => {
  return state.set('messaging', new ReviewsMessaging(fromJS(payload)))
}

const handlePostOrPutReviewsMessaging = (state, { payload: { request: { helper } } }) => {
  return state.setIn(['helper', 'requestElement'], fromJS(helper))
}

const handlePostOrPutReviewsMessagingSuccess = state => {
  const requestElement = state.getIn(['helper', 'requestElement'])
  const currentTab = requestElement.get('currentTab')
  const data = requestElement.get('data')
  return state.setIn(
    ['messaging', currentTab],
    new Messaging(
      Map({
        subject: data.get(`${currentTab}Subject`),
        text: data.get(`${currentTab}Text`),
        image: data.get(`${currentTab}Image`),
      })
    )
  )
}

const handlePostReviewsUserReviewers = (state, { payload: { request: { helper: { userId, reviewField }, data } } }) => {
  return state.setIn(['helper', 'requestElement'], fromJS({ data, userId, reviewField }))
}

const handlePostReviewsUserReviewersSuccess = state => {
  const requestElement = state.getIn(['helper', 'requestElement'])
  const index = state.get('users').findIndex(user => {
    return user.get('id') === requestElement.get('userId')
  })
  return state.setIn(
    ['users', index, requestElement.get('reviewField')],
    List(
      requestElement.getIn(['data', 'reviewers']).map(reviewer => {
        return new ReviewersUser(reviewer)
      })
    )
  )
}

const handleUpdateUserTeamsSuccessForReviews = (state, { payload }) => {
  const { userId, teams } = payload
  const index = state.get('users').findIndex(user => {
    return user.get('id') === userId
  })
  return state.setIn(['users', index, 'teams'], teams)
}

const handleSelectPage = (state, { payload }) => {
  return state.setIn(['pagination', 'current'], payload)
}

const handleFetchReviewsReportSuccess = (state, { payload }) => {
  return state.setIn(['report', 'data'], Map(payload)).setIn(['report', 'pending'], false)
}

const handleFetchReviewsReport = state => {
  return state.setIn(['report', 'pending'], true)
}

const handleFetchReviewsReportFail = state => {
  return state.setIn(['report', 'pending'], false)
}

const handlePostReviewsReportBulk = (state, { payload: { request: { helper } } }) => {
  return state.set('helper', fromJS(helper))
}

const handlePostReviewsReportBulkSuccess = state => {
  const reviewType = state.getIn(['helper', 'reviewType'])
  const emails = state.getIn(['helper', 'data', 'users'])
  const users = state.get('users')
  const newUsers = users.map(user => {
    if (emails.indexOf(user.get('email')) !== -1) {
      return user.set(`${reviewType}ReportCreated`, true)
    }
    return user
  })
  return state.set('users', newUsers)
}

const handleUpdateReviewUserEmail = (state, { payload }) => {
  const { id, email } = payload
  const index = state.get('users').findIndex(user => {
    return user.get('id') === id
  })
  return state.setIn(['users', index, 'email'], email)
}

const handleDeleteOrganizationUserSuccess = (state, { payload }) => {
  const { data: { userIds, from } } = payload
  const isDeletedOneUser = userIds.length === 1
  if (isDeletedOneUser && (from === EnumDeleteUserFrom.both || from === EnumDeleteUserFrom.reviews)) {
    const index = state.get('users').findIndex(user => {
      return user.get('id') === userIds[0]
    })
    return state.deleteIn(['users', index])
  }
  return state
}

export default handleActions(
  {
    [REHYDRATE]: handleRehydrate,
    [setFilterBySearch]: handleSetFilterBySearch,
    [setFilterByTeam]: handleSetFilterByTeam,
    [setFilterByStatus]: handleSetFilterByStatus,
    [setFilterBySort]: handleSetFilterBySort,
    [getReviewsUsers.success]: handleGetReviewsUsersSuccess,
    [getReviewsMessaging.success]: handleGetReviewsMessagingSuccess,
    [postOrPutReviewsMessaging]: handlePostOrPutReviewsMessaging,
    [postOrPutReviewsMessaging.success]: handlePostOrPutReviewsMessagingSuccess,
    [postReviewsUserReviewers]: handlePostReviewsUserReviewers,
    [postReviewsUserReviewers.success]: handlePostReviewsUserReviewersSuccess,
    [updateUserTeamsSuccessForReviews.toString()]: handleUpdateUserTeamsSuccessForReviews,
    [selectPage.toString()]: handleSelectPage,
    [fetchReviewsReport]: handleFetchReviewsReport,
    [fetchReviewsReport.success]: handleFetchReviewsReportSuccess,
    [fetchReviewsReport.fail]: handleFetchReviewsReportFail,
    [postReviewsReportBulk]: handlePostReviewsReportBulk,
    [postReviewsReportBulk.success]: handlePostReviewsReportBulkSuccess,
    [updateReviewUserEmail.success]: handleUpdateReviewUserEmail,
    [deleteOrganizationUserBulk.success]: handleDeleteOrganizationUserSuccess,
  },
  initialState
)
